import React, { useEffect, useRef } from 'react';
import lodash from 'lodash';
import {
  useForm,
  UseFormProps,
  FieldValues,
  DefaultValues,
  KeepStateOptions,
  Path
} from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { isNullUndefined, removeEmptyValues } from '../services/utils';
import { KeyValue, KeyValueDefault, KeyValueEnum } from '../models/common';
import { uppercaseFirstLetter } from '../services/utilsString';
import { Guid } from 'guid-typescript';
import { REFRESH_FORCE } from '../services/constants/constantsQueryParams';

type KeysWithStringValues<T> = {
  [key in keyof T]: T[key] extends
  | KeyValue
  | null
  | KeyValueDefault
  | KeyValueEnum
  ? key
  : never;
}[keyof T];
type IDomainRedux = {
  data: KeyValue[] | null;
  isLoading: boolean;
  isError: boolean;
  errorMessages: unknown;
};
type IObjectDomainRedux<T> = {
  [key in KeysWithStringValues<T>]: IDomainRedux;
};

interface IUseFormQueryParamsProps<T extends FieldValues, TOutput extends FieldValues = T>
  extends UseFormProps<T, any, TOutput> {
  mapDefaultValues?: IObjectDomainRedux<T>;
  validQueryParams?: { [key in keyof T]?: T[key] };
  autoSearchByUrl?: boolean;
  onSubmit: (data: TOutput, isQueryParams?: boolean) => void;
}
// NOTATION: This constant is used to set the keys that are date in the form
const KEYS_FORM_DATE = ['startDate', 'finalDate', 'endDate'];
const separatorQueryParamObject_three = '___';
const separatorQueryParamObject_two = '__';
const separatorQueryParamObject_one = '_';

function useFormQueryParams<T extends FieldValues, TOutput extends FieldValues = T>({
  mapDefaultValues,
  validQueryParams,
  autoSearchByUrl = true,
  onSubmit,
  ...useFormProps
}: IUseFormQueryParamsProps<T, TOutput>) {
  const methods = useForm<T, any, TOutput>(useFormProps);
  const cloneDefaultValuesRef = useRef(lodash.cloneDeep(useFormProps.defaultValues) as T);
  const cloneDefaultValues = cloneDefaultValuesRef.current;
  const [searchParams, setSearchParams] = useSearchParams();
  const isSubmittedByScreen = useRef<null | boolean>(null);
  const domainsFinishedLoading = useRef<null | string[]>(null);

  const suppressNextUrlSubmitRef = useRef(false);
  const isUrlSubmitRef = useRef(false);

  function parseKey(key: string, value: unknown) {
    const isValueString = typeof value === 'string';
    const isValueDate = value instanceof Date || KEYS_FORM_DATE.includes(key);
    const isValueNumber = typeof value === 'number';
    const isBoolean = typeof value === 'boolean';
    const isListKeyValue = Array.isArray(value);

    if (isListKeyValue) return `arrayListKeyValue${uppercaseFirstLetter(key)}`;

    if (isValueString || isValueDate || isValueNumber || isBoolean) return key;
    return `object${uppercaseFirstLetter(key)}`;
  }

  function parseValue(
    valueQueryParams: string | null,
    defaultValueForm: unknown,
    keyQueryParams?: string
  ) {
    const isListKeyValue = keyQueryParams?.includes('arrayListKeyValue');
    const isValueStringDate =
      typeof valueQueryParams === 'string' &&
      typeof defaultValueForm === 'object' &&
      (defaultValueForm instanceof Date ||
        KEYS_FORM_DATE.includes(String(keyQueryParams)));
    const isStringBoolean =
      typeof valueQueryParams === 'string' &&
      typeof defaultValueForm === 'boolean';
    const isObjectValue = keyQueryParams?.includes('object');

    if (isValueStringDate)
      return isNullUndefined(valueQueryParams)
        ? defaultValueForm
        : new Date(valueQueryParams);
    if (isStringBoolean) return valueQueryParams === 'true';

    if (isObjectValue) {
      const fields =
        valueQueryParams?.split(separatorQueryParamObject_two) ?? [];

      const obj = fields.reduce((acc, pair) => {
        const [k, v] = pair.split(separatorQueryParamObject_one);
        acc[k] = v;
        return acc;
      }, {} as Record<string, string>);

      return {
        ...obj,
        isQueryParams: true
      };
    }

    if (isListKeyValue) {
      const resultList = valueQueryParams
        ?.split(separatorQueryParamObject_three)
        .map(item => {
          const obj: Record<string, string> = {};

          item.split(separatorQueryParamObject_two).forEach(x => {
            const [key, value] = x.split(separatorQueryParamObject_one);
            obj[key] = value;
          });

          return obj;
        });

      return resultList;
    }

    return valueQueryParams;
  }

  /**
   * Notation: This React.Memo is used to get the values of the query params and set in the form
   */
  function queryParamsValuesFunction() {
    if (cloneDefaultValues === undefined) return {};

    const result = Object.entries(cloneDefaultValues).reduce(
      (acc, [key, value]) => {
        // Null defaults are ambiguous: could be a primitive field (string) or a KeyValue field.
        // Check the URL to determine which format was used when saving.
        if (value === null) {
          const arrayKey = `arrayListKeyValue${uppercaseFirstLetter(key)}`;
          const objectKey = `object${uppercaseFirstLetter(key)}`;
          if (searchParams.has(arrayKey)) {
            const rawValue = searchParams.get(arrayKey);
            const formattedValue = parseValue(rawValue, value, arrayKey);
            if (!isNullUndefined(formattedValue)) acc[key] = formattedValue;
          } else if (searchParams.has(objectKey)) {
            const rawValue = searchParams.get(objectKey);
            const formattedValue = parseValue(rawValue, value, objectKey);
            if (!isNullUndefined(formattedValue)) acc[key] = formattedValue;
          } else if (searchParams.has(key)) {
            const rawValue = searchParams.get(key);
            const formattedValue = parseValue(rawValue, value, key);
            if (!isNullUndefined(formattedValue)) acc[key] = formattedValue;
          }
          return acc;
        }

        const keyQueryParams = parseKey(key, value);
        const haveQueryParams = searchParams.has(keyQueryParams);

        if (value !== undefined && haveQueryParams) {
          const valueQueryParams = searchParams.get(keyQueryParams);
          const formattedValue = parseValue(
            valueQueryParams,
            value,
            keyQueryParams
          );

          acc[key] = formattedValue || value;
        }

        return acc;
      },
      {} as Record<string, any>
    );

    return removeEmptyValues(result);
  }
  const queryParamsValues = queryParamsValuesFunction();

  const filteredMapDefaultValues = React.useMemo(() => {
    if (mapDefaultValues === undefined) return {};
    const keysQueryParamsValues = Object.keys(queryParamsValues);

    const result = Object.keys(mapDefaultValues).reduce((acc, key) => {
      const haveInQueryParams = keysQueryParamsValues.includes(key);
      if (haveInQueryParams) {
        acc[key] = mapDefaultValues[key as KeysWithStringValues<T>];
      }
      return acc;
    }, {} as Record<string, IDomainRedux>);

    return removeEmptyValues(result);
  }, [JSON.stringify(mapDefaultValues), JSON.stringify(queryParamsValues)]);

  const filteredQueryParamsValues = React.useMemo(() => {
    return Object.entries(queryParamsValues).reduce((acc, [key, value]) => {
      const valueDefault = cloneDefaultValues[key];
      if (typeof valueDefault === 'string' || typeof valueDefault === 'number' || typeof valueDefault === 'boolean') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);
  }, [JSON.stringify(queryParamsValues)]);

  function updateQueryParams(data: Record<string, unknown>) {
    // filter data only with exists in cloneDefaultValues
    const dataFiltered = Object.entries(data).reduce((acc, [key, value]) => {
      if (!isNullUndefined(value)) {
        const isListKeyValue = Array.isArray(value);
        const isKeyValue = typeof value === 'object' && value !== null && 'key' in value;

        if (isKeyValue) {
          const nameKey = `object${uppercaseFirstLetter(key)}`;
          const flatString = Object.entries(value as Record<string, string>)
            .map(([k, v]) => `${k}${separatorQueryParamObject_one}${v}`)
            .join(separatorQueryParamObject_two);
          acc[nameKey] = flatString;
        } else if (isListKeyValue) {
          const nameKey = `arrayListKeyValue${uppercaseFirstLetter(key)}`;

          const mapperObjects = (value as Record<string, string>[])
            .map(item => {
              const resultMapper = Object.keys(item)
                .map(
                  key => `${key}${separatorQueryParamObject_one}${item[key]}`
                )
                .join(separatorQueryParamObject_two);

              return resultMapper;
            })
            .join(separatorQueryParamObject_three);

          acc[nameKey] = mapperObjects;
        } else {
          acc[key] = String(value);
        }
      }
      return acc;
    }, {} as Record<string, string>);

    suppressNextUrlSubmitRef.current = true;
    setSearchParams(dataFiltered, { replace: true });
  }
  function clearQueryParams() {
    setSearchParams({}, { replace: true });
    domainsFinishedLoading.current = null;
  }
  const customHandleSubmit = methods.handleSubmit(
    async (data) => {
      try {
        const isQueryParams = isUrlSubmitRef.current;
        isUrlSubmitRef.current = false;
        if (!isQueryParams) {
          isSubmittedByScreen.current = true;
        }
        await onSubmit(data, isQueryParams);
        // Notation: This code is used to convert the date to string
        const valuesDate = Object.entries(data).reduce((acc, [key, value]) => {
          const isValueDate = value instanceof Date;
          if (isValueDate) {
            acc[key] = value.toISOString();
          }
          return acc;
        }, {} as Record<string, string>);
        const formattedData = { ...data, ...valuesDate };

        updateQueryParams(removeEmptyValues(formattedData));
        isSubmittedByScreen.current = false;
      } catch {
        clearQueryParams();
      }
    },
    () => { }
  );

  function updateFormWithQueryParams() {
    if (mapDefaultValues === undefined) return;
    Object.entries(filteredMapDefaultValues).forEach(([key, value]) => {
      const mappedQueryParams = queryParamsValues[key];

      if (isNullUndefined(mappedQueryParams)) return;
      const formValue = methods.getValues(key as Path<T>) as KeyValue | null | undefined;
      const haveValue =
        !isNullUndefined(value.data) &&
        value.data.length > 0 &&
        !value.isLoading;
      const haveFinishedLoading =
        domainsFinishedLoading.current !== null &&
        domainsFinishedLoading.current.includes(key);
      const isInitialLoading =
        !isNullUndefined(formValue) &&
        haveFinishedLoading &&
        formValue.value === '';
      if (domainsFinishedLoading.current !== null && isInitialLoading)
        domainsFinishedLoading.current = domainsFinishedLoading.current?.filter(
          item => item !== key
        );
      if (!haveValue || (haveFinishedLoading && !isInitialLoading)) return;

      domainsFinishedLoading.current =
        domainsFinishedLoading.current === null
          ? [key]
          : [...domainsFinishedLoading.current, key];

      if (Array.isArray(mappedQueryParams)) {
        methods.setValue(key as Path<T>, mappedQueryParams as T[Path<T>]);
        return;
      }

      const keyFormatted = Guid.isGuid(mappedQueryParams.key)
        ? mappedQueryParams.key
        : Number(mappedQueryParams.key);
      const findParamInDomain = value.data?.find(
        (item: KeyValue) => item.key == keyFormatted
      );
      if (isNullUndefined(findParamInDomain)) return; // Notation: This code set empty value in the form
      const keyValueQueryParams = {
        ...findParamInDomain,
        isQueryParams: true
      };
      methods.setValue(key as Path<T>, keyValueQueryParams as T[Path<T>]);
    });
  }

  function onSubmitByQueryParams(queryValues: Record<string, unknown>) {
    if (suppressNextUrlSubmitRef.current) {
      suppressNextUrlSubmitRef.current = false;
      return;
    }

    const isEmptyQueryParams = Object.keys(queryValues).length === 0;
    if (
      isEmptyQueryParams ||
      methods.formState.isSubmitting ||
      isSubmittedByScreen.current
    )
      return;

    const haveValidQueryParams = !isNullUndefined(validQueryParams);
    if (haveValidQueryParams) {
      const haveAllValidQueryParams =
        !isNullUndefined(validQueryParams) &&
        Object.keys(validQueryParams).every(
          key => queryParamsValues[key] == validQueryParams[key]
        );
      if (!haveAllValidQueryParams) return;
    }
    methods.reset();

    Object.entries(queryValues).forEach(([key, value]) => {
      methods.setValue(key as Path<T>, value as T[Path<T>]);
    });

    isUrlSubmitRef.current = true;
    customHandleSubmit();
  }
  //#region UseEffect
  const hasRefreshForce = searchParams.has(REFRESH_FORCE)
    ? searchParams.get(REFRESH_FORCE)
    : null;
  // Notation: This useEffect call the customHandleSubmit when the queryParamsValues change

  useEffect(() => {
    if (autoSearchByUrl && !isNullUndefined(hasRefreshForce)) {
      onSubmitByQueryParams(queryParamsValues);
    }
  }, [hasRefreshForce]);

  useEffect(() => {
    if (autoSearchByUrl) {
      onSubmitByQueryParams(queryParamsValues);
    }
  }, [JSON.stringify(queryParamsValues)]);

  useEffect(() => {
    updateFormWithQueryParams();
  }, [
    JSON.stringify(filteredMapDefaultValues),
    JSON.stringify(queryParamsValues)
  ]);

  useEffect(() => {
    return () => {
      domainsFinishedLoading.current = null;
    };
  }, []);
  //#endregion

  function customReset(
    values?: DefaultValues<T>,
    keepStateOptions?: KeepStateOptions
  ) {
    methods.reset(values, keepStateOptions);
    clearQueryParams();
  }

  return {
    ...methods,
    reset: customReset,
    queryParamsValues,
    filteredQueryParamsValues,
    filteredMapDefaultValues,
    onSubmitByQueryParams,
    clearQueryParams,
    updateQueryParams,
    updateFormWithQueryParams,
    customHandleSubmit
  };
}

export default useFormQueryParams;
